import Link from 'next/link';
import Linkify from 'react-linkify';
import styled, { css } from 'styled-components';

import * as API from '~/_legacy/api';
import { Avatar } from '~/_legacy/components/atoms/avatar/index';
import { CompanyLogo } from '~/_legacy/components/atoms/companyLogo';
import type * as Models from '~/_legacy/models';

import * as COLORS from '~/colors';
import { Futura } from '~/styles';
import { media } from '~/utils';

type Props = {
  review?: Models.Review;
  withOrg?: Models.Org;
  onDelete?: () => void;
};

export function CardReview({ review, withOrg, onDelete }: Props) {
  const onClose = () => {
    if (!review) return;
    if (window.confirm('投稿を削除しますか？')) {
      API.Review.delete(review.id).then(() => {
        onDelete?.();
      });
    }
  };

  if (!review) {
    return (
      <Wrapper>
        <Cover />
        <Main>
          <Left>
            <ReviewImageWrapper>
              <Avatar size={'50px'} />
            </ReviewImageWrapper>
          </Left>
          <Right>
            <EmptyName />
            <EmptyTime />
            <div>
              <EmptyText />
              <EmptyText />
              <EmptyText />
            </div>
          </Right>
        </Main>
      </Wrapper>
    );
  }

  const { org } = review;
  const color = withOrg && org && org.sdgs ? COLORS.sdgs[org.sdgs[0]] : null;

  const orgComponent =
    withOrg && org ? (
      <OrgWrapper>
        <Link href={`/orgs/${org.uid}`}>
          <Org>
            <At>@</At>
            <CompanyLogo src={org.logo} alt='org-logo' size={42} />
            <div>
              <OrgCategory>{org.category}</OrgCategory>
              <OrgName>{org.name}</OrgName>
            </div>
          </Org>
        </Link>
      </OrgWrapper>
    ) : null;

  return (
    <Wrapper>
      <Main start={color?.start} end={color?.end}>
        <Left>
          <ReviewImageWrapper>
            <Avatar size={'50px'} src={review.postableAvatar()} />
          </ReviewImageWrapper>
        </Left>
        <Right>
          <Name>{review.postableName()} さん</Name>
          <Time>{review.displayCreatedAt()}</Time>
          <Text>
            <Linkify
              componentDecorator={(decoratedHref, decoratedText, key) => (
                <a target='blank' href={decoratedHref} key={key}>
                  {decoratedText}
                </a>
              )}
            >
              {review.text}
            </Linkify>
          </Text>
          {review.images.map((image: any, index: number) => (
            <Image src={image} alt={`image-${index}`} key={index} />
          ))}
        </Right>
        {review.deletable && <CloseButton onClick={onClose} src='/images/close.svg' />}
      </Main>
      {orgComponent}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-top: 20px;
  position: relative;
  border-left: 7px solid #585858;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.26);
`;

const Cover = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #f2f2f2;
  z-index: 1;
  opacity: 0.7;
`;

const Main = styled.div<{ start?: string; end?: string }>`
  background-color: white;
  display: flex;
  flex-direction: row;
  color: #505050;
  position: relative;

  ${(props) =>
    props.start && props.end
      ? css`
          &:after {
            content: '';
            display: block;
            height: 3px;
            width: 100%;
            background: linear-gradient(
              to left,
              ${(props: { start?: string; end?: string }) => props.start},
              ${(props: { start?: string; end?: string }) => props.end}
            );
            position: absolute;
            left: 0;
            bottom: 0;
          }
        `
      : ''}
`;

const Left = styled.div``;

const ReviewImageWrapper = styled.div`
  margin-top: 20px;
  margin-left: 20px;
`;

const Right = styled.div`
  flex: 1;
  padding: 25px 25px 35px 25px;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: bold;
`;

const EmptyName = styled.div`
  width: 200px;
  height: 14px;
  background-color: #e6e6e6;
`;

const Time = styled.div`
  margin-top: 10px;
  font-size: 12px;
`;

const EmptyTime = styled.div`
  margin-top: 10px;
  height: 12px;
  width: 200px;
  background-color: #e6e6e6;
`;

const Text = styled.div`
  margin-top: 15px;
  font-size: 16px;
  line-height: 25px;
  text-align: justify;

  a {
    color: #213d98;
    text-decoration: underline;
    word-break: break-all;
  }
`;

const EmptyText = styled.div`
  width: 100%;
  margin-top: 5px;
  height: 16px;
  background-color: #e6e6e6;

  &:first-child {
    margin-top: 15px;
  }
`;

const OrgWrapper = styled.div`
  display: flex;
  flex-direction: row;
  background: #f0f0f0;
  height: 60px;
  align-items: center;
`;

const Org = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const At = styled(Futura)`
  margin-left: 40px;
  margin-right: 10px;
  font-size: 20px;
  font-weight: normal;
`;

const OrgCategory = styled.div`
  margin-left: 15px;
  font-size: 12px;
  color: #1a1a1a;
`;

const OrgName = styled.div`
  margin-top: 5px;
  margin-left: 15px;
  font-size: 14px;
  color: #1a1a1a;
  font-weight: bold;
`;

const CloseButton = styled.img`
  position: absolute;
  top: 3px;
  right: 3px;
  width: 20px;
  cursor: pointer;
`;

const Image = styled.img`
  margin-top: 20px;
  width: 100%;
  height: 150px;
  object-fit: contain;
  object-position: left top;

  ${media.mid`
    heighT: 100px;
  `}
`;
